import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Check, Loader2, ArrowLeft, Sparkles, Zap, Shield, Clock, CreditCard, Building2, Calendar, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';
const API_URL = `${BACKEND_URL}/api`;

// Pricing constants
const MONTHLY_PRICE_NET = 55;
const YEARLY_DISCOUNT = 0.15;   // 15%
const HALFYEARLY_DISCOUNT = 0.07; // 7%
const VAT_RATE = 0.23;

const MONTHLY_PRICE_GROSS = Math.round(MONTHLY_PRICE_NET * (1 + VAT_RATE) * 100) / 100;
const YEARLY_PRICE_NET = Math.round(MONTHLY_PRICE_NET * 12 * (1 - YEARLY_DISCOUNT) * 100) / 100;
const YEARLY_PRICE_GROSS = Math.round(YEARLY_PRICE_NET * (1 + VAT_RATE) * 100) / 100;
const YEARLY_MONTHLY_EQUIVALENT = Math.round(YEARLY_PRICE_NET / 12 * 100) / 100;
const HALFYEARLY_PRICE_NET = Math.round(MONTHLY_PRICE_NET * 6 * (1 - HALFYEARLY_DISCOUNT) * 100) / 100;
const HALFYEARLY_PRICE_GROSS = Math.round(HALFYEARLY_PRICE_NET * (1 + VAT_RATE) * 100) / 100;
const HALFYEARLY_MONTHLY_EQUIVALENT = Math.round(HALFYEARLY_PRICE_NET / 6 * 100) / 100;

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const planFromUrl = searchParams.get('plan');
  const [selectedPlan, setSelectedPlan] = useState(planFromUrl === 'monthly' ? 'monthly' : planFromUrl === 'halfyearly' ? 'halfyearly' : 'yearly');

  useEffect(() => {
    if (user) {
      loadSubscriptionStatus();
    }
  }, [user]);

  const loadSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_URL}/payments/tpay/checkout`,
        { 
          origin_url: originUrl,
          plan_type: selectedPlan 
        },
        { 
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error('Nie udało się rozpocząć procesu płatności');
      console.error('Payment error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (statusLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-10 h-10 animate-spin text-primary-500" />
          <p className="text-muted-foreground">Ładowanie...</p>
        </div>
      </div>
    );
  }

  const isActive = subscriptionStatus?.status === 'active' || subscriptionStatus?.status === 'trial';
  const daysLeft = subscriptionStatus?.days_left || 0;
  const currentPrice = selectedPlan === 'yearly' ? YEARLY_PRICE_GROSS : selectedPlan === 'halfyearly' ? HALFYEARLY_PRICE_GROSS : MONTHLY_PRICE_GROSS;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-orange-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/dashboard')} 
              variant="ghost" 
              size="sm"
              className="gap-2 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <h1 className="font-heading text-xl text-primary-900 dark:text-slate-100 font-bold">Subskrypcja</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-lg bg-white dark:bg-slate-800 shadow-md flex items-center justify-center overflow-hidden">
              <img src="/kai-logo.png" alt="KAI" className="w-12 h-12 object-contain rounded-lg" />
            </div>
            <span className="font-heading text-primary-900 dark:text-slate-100 font-bold">KAI</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Subscription Status Card */}
        {subscriptionStatus && (
          <div className={`mb-10 rounded-2xl p-6 shadow-lg border ${
            isActive 
              ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800' 
              : 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  isActive ? 'bg-green-500' : 'bg-amber-500'
                }`}>
                  {isActive ? (
                    <Check className="w-7 h-7 text-white" />
                  ) : (
                    <Clock className="w-7 h-7 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Status subskrypcji</p>
                  <p className={`text-xl font-bold ${isActive ? 'text-green-700' : 'text-amber-700'}`}>
                    {subscriptionStatus.status === 'trial' ? 'Okres próbny' : 
                     subscriptionStatus.status === 'active' ? 'Aktywna' : 'Nieaktywna'}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600 mb-1">Pozostało</p>
                <p className={`text-3xl font-bold ${isActive ? 'text-green-700' : 'text-amber-700'}`}>
                  {daysLeft} <span className="text-lg font-normal">{daysLeft === 1 ? 'dzień' : 'dni'}</span>
                </p>
              </div>
            </div>
            {!isActive && (
              <div className="mt-4 p-4 bg-red-100/50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700 font-medium">
                  ⚠️ Twoja subskrypcja wygasła. Wykup nową, aby kontynuować korzystanie z KAI.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Pricing Section Header */}
        <div className="text-center mb-6 lg:mb-10 px-4">
          <h1 className="font-heading text-2xl sm:text-4xl lg:text-5xl text-primary-900 dark:text-slate-100 mb-3 lg:mb-4 font-bold">
            Wybierz swój plan
          </h1>
          <p className="text-sm lg:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            KAI to Twój inteligentny asystent księgowy. Zaoszczędź 7% wybierając plan półroczny lub 15% z planem rocznym!
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 lg:gap-6 max-w-5xl mx-auto mb-6 lg:mb-10 px-4">
          {/* Monthly Plan */}
          <button
            data-testid="plan-monthly"
            onClick={() => setSelectedPlan('monthly')}
            className={`relative bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl shadow-lg overflow-hidden border-2 transition-all text-left ${
              selectedPlan === 'monthly'
                ? 'border-primary-500 shadow-xl scale-[1.02]'
                : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-500'
            }`}
          >
            {selectedPlan === 'monthly' && (
              <div className="absolute top-3 lg:top-4 right-3 lg:right-4 w-5 lg:w-6 h-5 lg:h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <Check className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
              </div>
            )}
            <div className="p-4 lg:p-6">
              <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-lg lg:rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                  <Calendar className="w-5 lg:w-6 h-5 lg:h-6 text-slate-600 dark:text-slate-300" />
                </div>
                <div>
                  <h3 className="font-bold text-base lg:text-lg text-slate-900 dark:text-slate-100">Plan Miesięczny</h3>
                  <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">Elastyczna opcja</p>
                </div>
              </div>
              <div className="mb-3 lg:mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl lg:text-4xl font-bold text-slate-900 dark:text-slate-100">{MONTHLY_PRICE_NET}</span>
                  <span className="text-base lg:text-lg text-slate-500 dark:text-slate-400">zł</span>
                </div>
                <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">netto / miesiąc ({MONTHLY_PRICE_GROSS} zł brutto)</p>
              </div>
              <ul className="space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Pełny dostęp do wszystkich funkcji
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Możliwość anulowania w każdej chwili
                </li>
              </ul>
            </div>
          </button>

          {/* Half-Yearly Plan */}
          <button
            data-testid="plan-halfyearly"
            onClick={() => setSelectedPlan('halfyearly')}
            className={`relative bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl shadow-lg overflow-hidden border-2 transition-all text-left ${
              selectedPlan === 'halfyearly'
                ? 'border-primary-500 shadow-xl scale-[1.02]'
                : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-500'
            }`}
          >
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-center py-1 lg:py-1.5 text-xs lg:text-sm font-semibold">
              <Gift className="w-3 lg:w-4 h-3 lg:h-4 inline mr-1" />
              Oszczędź 7%!
            </div>
            {selectedPlan === 'halfyearly' && (
              <div className="absolute top-8 lg:top-10 right-3 lg:right-4 w-5 lg:w-6 h-5 lg:h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <Check className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
              </div>
            )}
            <div className="p-4 lg:p-6 pt-10 lg:pt-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-lg lg:rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Zap className="w-5 lg:w-6 h-5 lg:h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="font-bold text-base lg:text-lg text-slate-900 dark:text-slate-100">Plan Półroczny</h3>
                  <p className="text-xs lg:text-sm text-blue-600 dark:text-blue-400 font-medium">7% taniej!</p>
                </div>
              </div>
              <div className="mb-3 lg:mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl lg:text-4xl font-bold text-slate-900 dark:text-slate-100">{HALFYEARLY_PRICE_NET}</span>
                  <span className="text-base lg:text-lg text-slate-500 dark:text-slate-400">zł</span>
                </div>
                <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">netto / 6 mies. ({HALFYEARLY_PRICE_GROSS} zł brutto)</p>
                <p className="text-xs lg:text-sm text-blue-600 dark:text-blue-400 font-medium mt-1">
                  Tylko {HALFYEARLY_MONTHLY_EQUIVALENT} zł / miesiąc
                </p>
              </div>
              <ul className="space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Pełny dostęp do wszystkich funkcji
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Oszczędność {Math.round(MONTHLY_PRICE_NET * 6 - HALFYEARLY_PRICE_NET)} zł za pół roku
                </li>
              </ul>
            </div>
          </button>

          {/* Yearly Plan */}
          <button
            data-testid="plan-yearly"
            onClick={() => setSelectedPlan('yearly')}
            className={`relative bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl shadow-lg overflow-hidden border-2 transition-all text-left ${
              selectedPlan === 'yearly'
                ? 'border-primary-500 shadow-xl scale-[1.02]'
                : 'border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-500'
            }`}
          >
            {/* Best Value Badge */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-center py-1 lg:py-1.5 text-xs lg:text-sm font-semibold">
              <Gift className="w-3 lg:w-4 h-3 lg:h-4 inline mr-1" />
              Oszczędź 15%!
            </div>
            {selectedPlan === 'yearly' && (
              <div className="absolute top-8 lg:top-10 right-3 lg:right-4 w-5 lg:w-6 h-5 lg:h-6 bg-primary-500 rounded-full flex items-center justify-center">
                <Check className="w-3 lg:w-4 h-3 lg:h-4 text-white" />
              </div>
            )}
            <div className="p-4 lg:p-6 pt-10 lg:pt-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-lg lg:rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Sparkles className="w-5 lg:w-6 h-5 lg:h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="font-bold text-base lg:text-lg text-slate-900 dark:text-slate-100">Plan Roczny</h3>
                  <p className="text-xs lg:text-sm text-green-600 dark:text-green-400 font-medium">15% taniej!</p>
                </div>
              </div>
              <div className="mb-3 lg:mb-4">
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl lg:text-4xl font-bold text-slate-900 dark:text-slate-100">{YEARLY_PRICE_NET}</span>
                  <span className="text-base lg:text-lg text-slate-500 dark:text-slate-400">zł</span>
                </div>
                <p className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">netto / rok ({YEARLY_PRICE_GROSS} zł brutto)</p>
                <p className="text-xs lg:text-sm text-green-600 dark:text-green-400 font-medium mt-1">
                  Tylko {YEARLY_MONTHLY_EQUIVALENT} zł / miesiąc
                </p>
              </div>
              <ul className="space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-600 dark:text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Pełny dostęp do wszystkich funkcji
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Oszczędność {Math.round(MONTHLY_PRICE_NET * 12 - YEARLY_PRICE_NET)} zł rocznie
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-3 lg:w-4 h-3 lg:h-4 text-green-500 flex-shrink-0" />
                  Priorytetowe wsparcie
                </li>
              </ul>
            </div>
          </button>
        </div>

        {/* Features List */}
        <div className="bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 max-w-4xl mx-auto mb-6 lg:mb-8">
          <h3 className="font-bold text-base lg:text-lg text-slate-900 dark:text-slate-100 mb-3 lg:mb-4 text-center">Co zawiera każdy plan:</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-4">
            {[
              { icon: Sparkles, text: 'Nielimitowany dostęp do AI' },
              { icon: Zap, text: 'OpenAI, Claude, Gemini' },
              { icon: Clock, text: 'Historia bez limitu' },
              { icon: Shield, text: 'Cytowania prawne' },
              { icon: CreditCard, text: 'Faktury VAT' },
              { icon: Check, text: '7 dni próbnych' },
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 lg:gap-3">
                <div className="w-7 lg:w-8 h-7 lg:h-8 rounded-lg bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-3 lg:w-4 h-3 lg:h-4 text-primary-600 dark:text-primary-400" />
                </div>
                <span className="text-xs lg:text-sm text-slate-700 dark:text-slate-300">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payment Method Selection */}
        <div className="bg-white dark:bg-slate-800 rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6 max-w-lg mx-auto">
          <div className="flex items-center justify-center gap-2 lg:gap-3 mb-4 lg:mb-6 p-3 lg:p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg lg:rounded-xl border border-primary-100 dark:border-primary-800">
            <div className="w-10 lg:w-12 h-10 lg:h-12 rounded-lg lg:rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Building2 className="w-5 lg:w-6 h-5 lg:h-6 text-white" />
            </div>
            <div>
              <span className="font-semibold text-slate-800 dark:text-slate-100 block text-sm lg:text-base">Płatność przez Tpay</span>
              <span className="text-xs lg:text-sm text-slate-500 dark:text-slate-400">BLIK, przelew, karta</span>
            </div>
          </div>

          {/* Summary & Buy Button */}
          <div className="border-t border-slate-200 dark:border-slate-700 pt-3 lg:pt-4 mb-3 lg:mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm lg:text-base text-slate-600 dark:text-slate-400">Plan:</span>
              <span className="font-semibold text-sm lg:text-base text-slate-900 dark:text-slate-100">
                {selectedPlan === 'yearly' ? 'Roczny (15% rabat)' : 'Miesięczny'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm lg:text-base text-slate-600 dark:text-slate-400">Do zapłaty:</span>
              <span className="text-xl lg:text-2xl font-bold text-primary-600 dark:text-primary-400">{currentPrice} zł</span>
            </div>
          </div>

          <Button
            data-testid="subscribe-button"
            onClick={handleSubscribe}
            disabled={loading || (subscriptionStatus?.status === 'active' && subscriptionStatus?.plan_type !== 'trial')}
            className="w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-4 lg:py-6 text-base lg:text-lg rounded-lg lg:rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 lg:w-5 h-4 lg:h-5 animate-spin mr-2" />
                Przetwarzanie...
              </>
            ) : (subscriptionStatus?.status === 'active' && subscriptionStatus?.plan_type !== 'trial') ? (
              <>
                <Check className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                Subskrypcja Aktywna
              </>
            ) : (
              <>
                <CreditCard className="w-4 lg:w-5 h-4 lg:h-5 mr-2" />
                {subscriptionStatus?.status === 'trial' ? `Wykup plan - ${currentPrice} zł` : `Zapłać ${currentPrice} zł`}
              </>
            )}
          </Button>

          <div className="mt-3 lg:mt-4 flex items-center justify-center gap-2 text-xs lg:text-sm text-slate-500">
            <Shield className="w-3 lg:w-4 h-3 lg:h-4" />
            <span>Bezpieczne płatności przez Tpay</span>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 lg:mt-12 text-center space-x-4 lg:space-x-6 text-xs lg:text-sm">
          <a href="/terms" className="text-primary-600 hover:text-primary-800 hover:underline">Regulamin</a>
          <a href="/privacy" className="text-primary-600 hover:text-primary-800 hover:underline">Polityka Prywatności</a>
          <a href="/copyright" className="text-primary-600 hover:text-primary-800 hover:underline">Prawo Własności</a>
        </div>
      </div>
    </div>
  );
}
